import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { vec2extended as vec2x } from '@canvas/math/vec2';

import { createCubicBezier } from './bezier.ts';
import { type CubicBezier } from './types.ts';

/**
 * Defines the different modes for generating cubic bezier splines from points.
 */
export enum SplineMode {
  /**
   * Creates smooth, flowing curves by using full distances between points for control point placement.
   * This mode produces visually pleasing curves but may cause crossover when paths curve back on themselves.
   * Best suited for paths with gradual directional changes.
   */
  SMOOTH_SERIES = 'SMOOTH_SERIES',

  /**
   * Creates balanced curves that prevent crossover by projecting distances onto angle directions.
   * This mode uses dot product projection to constrain control points, preventing curves from
   * crossing over each other at sharp turns or when paths curve back on themselves.
   * Best suited for paths with sharp directional changes or potential self-intersection.
   */
  BALANCED_CURVATURE = 'BALANCED_CURVATURE',
}

const prevLineFlyweight = vec2.create();
const nextLineFlyweight = vec2.create();

function getTangentFromConsecutivePoints(
  out: vec2,
  prevPoint: ReadonlyVec2,
  currPoint: ReadonlyVec2,
  nextPoint: ReadonlyVec2,
): vec2 {
  const prevLine = vec2.sub(prevLineFlyweight, currPoint, prevPoint);
  const nextLine = vec2.sub(nextLineFlyweight, nextPoint, currPoint);
  vec2.normalize(prevLine, prevLine);
  vec2.normalize(nextLine, nextLine);
  vec2.add(out, prevLine, nextLine);
  vec2.normalize(out, out);
  return out;
}

const tangentFlyweight = vec2.create();

/**
 * Given a series of points, this derives a smooth series of cubic bezier curves
 * passing through each point.
 *
 * It is difficult to describe in words alone why certain choices are made in this algorithm
 * so run `yarn book` and visit
 * http://localhost:1337/#/playground-bezier--create-smooth-cubic-bezier-spline-from-points.html
 * and play with it to form a visual idea of what this does.
 *
 * This is intended to be used for deriving curved paths,
 * when the user selects the curved path presentation option.
 *
 * @param points ReadonlyArray<ReadonlyVec2>, series of points
 * @param startAngle (optional), if provided, the angle in radians of the start of the path
 * @param endAngle (optional), if provided, the angle in radians of the end of the path
 * @param broadness a value determining how broad the curves appear
 * @param debugControlPoints an optional value that mutates debugControlPoints to add control points
 * for visualisation in book.
 * @param mode the spline generation mode - controls how control points are calculated
 * @returns an array of cubic beziers
 */
export function createSmoothCubicBezierSplineFromPoints(
  points: ReadonlyArray<ReadonlyVec2>,
  startAngle?: number,
  endAngle?: number,
  broadness = isFeatureFlagEnabled('contentWrapperMainToolbarRefresh') ? 0.38 : 0.3,
  debugControlPoints?: vec2[],
  mode = SplineMode.SMOOTH_SERIES,
): CubicBezier[] {
  /**
   * explanation of naming in variables:
   * here is a badly drawn ascii code bezier spline made of 3 bezier curves
   *
   * ● = endpoints
   * ○ = waypoints
   * ◆ = bezier control points belonging to endpoints
   * ◇ = bezier control points belonging to waypoints
   *
   *   s     so
   *   ●--__◆
   *        \◇ w1i
   *         \
   *          ○ w1
   *           \              ei   e
   *       w1o ◇\__       ___--◆---●
   *             ◇ ---○--- ◇
   *           w2i    w2   w2o
   *
   * "s" = start point
   * "e" = end point
   * "i" = in (control point)
   * "o" = out (control point)
   * "w#" = waypoint
   *
   * "$Dist" = distance from $ to its control point
   * "$Tangent" = tangent from $ to its control point as a unit vector
   *
   * these short names are used because it makes for clearer explanation
   * and less verbose variable names
   */

  const pCount = points.length;

  // not enough points, return nothing
  if (pCount < 2) {
    return [];
  }

  // get start and end points
  const s = points[0];
  const e = points[pCount - 1];
  const sClone = vec2.clone(s);
  const eClone = vec2.clone(e);

  // if only 2 points and no angles are defined, make a straight bezier curve
  if (pCount === 2 && startAngle === undefined && endAngle === undefined) {
    return [createCubicBezier(sClone, eClone, sClone, eClone)];
  }

  // calculate so and ei (control points adjacent to end points, see diagram above)
  // as a distance from s and e, in the direction of its tangent
  // if soMyabe or ei Maybe are undefined it means that we can't know the position of the control point
  // until we iterate over all the points
  let soMaybe: vec2 | undefined;
  let eiMaybe: vec2 | undefined;

  if (startAngle !== undefined) {
    const sNext = points[1];
    const sTangent = vec2x.fromPolar(startAngle);
    const sDist = calculateEndControlPointDistance(s, sNext, sTangent, broadness, mode);
    soMaybe = vec2.scaleAndAdd(vec2.create(), s, sTangent, sDist);
  }
  if (endAngle !== undefined) {
    const ePrev = points[pCount - 2];
    const eTangent = vec2x.fromPolar(endAngle);
    const eDist = calculateEndControlPointDistance(e, ePrev, eTangent, broadness, mode);
    // Note: eDist should be negative for end tangent (going backwards)
    eiMaybe = vec2.scaleAndAdd(vec2.create(), e, eTangent, -eDist);
  }

  // iterate over waypoints, pushing all w#i and w#o control points to single array in order
  const controlPoints: vec2[] = [];

  for (let i = 1; i < pCount - 1; i++) {
    const prevPoint = points[i - 1];
    const currPoint = points[i];
    const nextPoint = points[i + 1];

    // determine where to roughly "point" the bezier handles
    // if we are on the first or last waypoint, we might point to
    // a control point ("ei" or "so") instead of the point
    const first = i === 1;
    const last = i === pCount - 2;
    const wiTarget = first && soMaybe ? soMaybe : prevPoint;
    const woTarget = last && eiMaybe ? eiMaybe : nextPoint;

    // find the final tangent that both in and out control points will share
    const tangent = getTangentFromConsecutivePoints(
      tangentFlyweight,
      wiTarget,
      currPoint,
      woTarget,
    );

    const wiDist = vec2.dist(prevPoint, currPoint) * -broadness;
    const woDist = vec2.dist(currPoint, nextPoint) * broadness;
    const wi = vec2.scaleAndAdd(vec2.create(), currPoint, tangent, wiDist);
    const wo = vec2.scaleAndAdd(vec2.create(), currPoint, tangent, woDist);
    controlPoints.push(wi, wo);
  }

  // add so control point to the controlPoints array
  const soAddControlPoint = () => {
    if (soMaybe) {
      controlPoints.unshift(soMaybe);
    } else {
      const w1i = controlPoints[0];
      const sDist = vec2.dist(s, w1i) * broadness;
      const so = vec2.sub(vec2.create(), w1i, s);
      vec2.normalize(so, so);
      vec2.scaleAndAdd(so, s, so, sDist);
      controlPoints.unshift(so);
    }
  };

  // add ei control point to the controlPoints array
  const eiAddControlPoint = () => {
    if (eiMaybe) {
      controlPoints.push(eiMaybe);
    } else {
      const wo = controlPoints[controlPoints.length - 1];
      const eDist = vec2.dist(e, wo) * broadness;
      const ei = vec2.sub(vec2.create(), wo, e);
      vec2.normalize(ei, ei);
      vec2.scaleAndAdd(ei, e, ei, eDist);
      controlPoints.push(ei);
    }
  };

  // a defined control point mst be added first
  // as these functions can reference each other's output
  if (!soMaybe && eiMaybe) {
    eiAddControlPoint();
    soAddControlPoint();
  } else {
    soAddControlPoint();
    eiAddControlPoint();
  }

  // iterate over points and pairs of control points to construct bezier curves
  const beziers: CubicBezier[] = [];
  for (let i = 0; i < pCount - 1; i++) {
    const currPoint = vec2.clone(points[i]);
    const nextPoint = vec2.clone(points[i + 1]);
    const control1 = controlPoints[i * 2];
    const control2 = controlPoints[i * 2 + 1];

    debugControlPoints?.push(control1, control2);
    beziers.push(createCubicBezier(currPoint, control1, control2, nextPoint));
  }

  return beziers;
}

const distanceVectorFlyweight = vec2.create();

/**
 * Calculates the distance for the control point
 * at either end of a spline based on the spline mode.
 *
 * @param endPoint the point on either end of the spline (start or end)
 * @param adjacentPoint the point adjacent to the end point
 * @param tangent the tangent direction
 * @param broadness the broadness factor for the curve
 * @param mode the spline generation mode
 * @returns the calculated distance for the start control point
 */
function calculateEndControlPointDistance(
  endPoint: ReadonlyVec2,
  adjacentPoint: ReadonlyVec2,
  tangent: ReadonlyVec2,
  broadness: number,
  mode: SplineMode,
): number {
  if (mode === SplineMode.BALANCED_CURVATURE) {
    // Use projection of distance vector onto the start angle direction
    const distanceVector = vec2.sub(distanceVectorFlyweight, adjacentPoint, endPoint);
    return Math.abs(vec2.dot(distanceVector, tangent)) * broadness;
  } else {
    return vec2.dist(endPoint, adjacentPoint) * broadness;
  }
}
